/**
 * Lists Module
 *
 * Sole owner of list-shaped data in localStorage. Other modules MUST
 * go through this module to create/read/update/delete lists; they
 * must not touch STORAGE_KEYS.LISTS directly.
 *
 * List type (mirrors design data model):
 *   {
 *     id: string,          // crypto.randomUUID()
 *     name: string,        // non-empty, trimmed
 *     items: string[],
 *     createdAt: number,
 *     updatedAt: number
 *   }
 */

import {
  safeRead,
  safeWrite,
  STORAGE_KEYS
} from './storage.js';
import {
  clearSession,
  hasActiveSessionForList
} from './session.js';

/**
 * Generate a UUID using the Web Crypto API.
 * crypto.randomUUID is available in all modern browsers and jsdom >= 20.
 * Falls back to a random-hex composition if somehow unavailable.
 * @returns {string}
 */
function uuid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback: 128-bit random hex in 8-4-4-4-12 shape (not RFC compliant
  // but uniqueness is sufficient for per-device list IDs).
  const bytes = new Uint8Array(16);
  const cryptoApi = typeof globalThis.crypto !== 'undefined' ? globalThis.crypto : null;
  if (cryptoApi && typeof cryptoApi.getRandomValues === 'function') {
    cryptoApi.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function readAll() {
  const raw = safeRead(STORAGE_KEYS.LISTS, []);
  return Array.isArray(raw) ? raw : [];
}

function writeAll(listsArray) {
  return safeWrite(STORAGE_KEYS.LISTS, listsArray);
}

/**
 * Return all lists sorted by updatedAt descending.
 * @returns {Array}
 */
export function getAllLists() {
  const all = readAll();
  return [...all].sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
}

/**
 * Return the list with the given id, or null.
 * @param {string} id
 * @returns {object | null}
 */
export function getListById(id) {
  const all = readAll();
  return all.find(l => l.id === id) || null;
}

/**
 * Create a new list. Assigns a UUID and timestamps.
 * @param {{name: string, items: string[]}} input
 * @returns {object} the created list
 */
export function createList({ name, items }) {
  const now = Date.now();
  const list = {
    id: uuid(),
    name: String(name).trim(),
    items: [...items],
    createdAt: now,
    updatedAt: now
  };
  const all = readAll();
  all.push(list);
  writeAll(all);
  return list;
}

/**
 * Update a list. Merges patch onto the existing list, bumps
 * updatedAt, and preserves id/createdAt.
 * @param {string} id
 * @param {Partial<{name: string, items: string[]}>} patch
 * @returns {object | null} the updated list or null if not found
 */
export function updateList(id, patch) {
  const all = readAll();
  const idx = all.findIndex(l => l.id === id);
  if (idx < 0) return null;

  const current = all[idx];
  const next = { ...current };
  if (typeof patch.name === 'string') {
    next.name = patch.name.trim();
  }
  if (Array.isArray(patch.items)) {
    next.items = [...patch.items];
  }
  // Always preserve immutable fields
  next.id = current.id;
  next.createdAt = current.createdAt;
  next.updatedAt = Date.now();

  all[idx] = next;
  writeAll(all);
  return next;
}

/**
 * Delete a list by id. Also clears the active session if it points
 * at this list (design invariant: deleting a list with a pointing
 * session clears the session).
 * Idempotent for unknown ids.
 * @param {string} id
 */
export function deleteList(id) {
  const all = readAll();
  const next = all.filter(l => l.id !== id);
  if (next.length !== all.length) {
    writeAll(next);
  }
  // Session-aware side effect.
  if (hasActiveSessionForList(id)) {
    clearSession();
  }
}

/**
 * Seed the example list if-and-only-if there are no lists yet.
 * Idempotent; returns the seeded list or null if no-op.
 *
 * Seed content finalized per design open questions.
 */
export const EXAMPLE_LIST_NAME = 'Example — edit or delete';
export const EXAMPLE_LIST_ITEMS = Object.freeze([
  'Try the Yes button',
  'Try the No button',
  'Long-press this list to edit or delete'
]);

/**
 * @returns {object | null}
 */
export function seedExampleListIfEmpty() {
  const all = readAll();
  if (all.length > 0) return null;
  return createList({
    name: EXAMPLE_LIST_NAME,
    items: [...EXAMPLE_LIST_ITEMS]
  });
}

/**
 * Export all lists as a pretty-printed JSON string for clipboard copy.
 * Ordering is the stored order (not the sorted view) so round-tripping
 * is stable.
 * @returns {string}
 */
export function exportListsAsJson() {
  const all = readAll();
  return JSON.stringify(all, null, 2);
}

function isValidIncomingList(x) {
  return (
    x !== null &&
    typeof x === 'object' &&
    typeof x.id === 'string' && x.id.length > 0 &&
    typeof x.name === 'string' && x.name.trim().length > 0 &&
    Array.isArray(x.items) && x.items.every(s => typeof s === 'string') &&
    typeof x.createdAt === 'number' && Number.isFinite(x.createdAt) &&
    typeof x.updatedAt === 'number' && Number.isFinite(x.updatedAt)
  );
}

/**
 * Import lists from a JSON string previously produced by
 * exportListsAsJson (or hand-edited to the same shape).
 *
 * Merge semantics:
 *   - An incoming list whose id matches an existing list replaces the
 *     existing one when its updatedAt is strictly greater; otherwise
 *     it is skipped (counts as `skipped`).
 *   - An incoming list whose id is new is appended.
 *   - Incoming entries that fail shape validation are ignored and
 *     counted as `skipped`.
 *
 * This makes repeated imports idempotent: pasting the same JSON twice
 * reports 0 imported / 0 updated the second time.
 *
 * Throws on malformed JSON or non-array top-level. Callers should
 * catch and surface a "That doesn't look like a valid export" toast.
 *
 * @param {string} json
 * @returns {{ imported: number, updated: number, skipped: number, writeOk: boolean }}
 */
export function importListsFromJson(json) {
  let parsed;
  try {
    parsed = JSON.parse(json);
  } catch (_e) {
    throw new Error('Invalid JSON');
  }
  if (!Array.isArray(parsed)) {
    throw new Error('Expected a JSON array of lists');
  }

  const existing = readAll();
  const byId = new Map(existing.map(l => [l.id, l]));
  let imported = 0, updated = 0, skipped = 0;

  for (const raw of parsed) {
    if (!isValidIncomingList(raw)) { skipped += 1; continue; }
    const incoming = {
      id: raw.id,
      name: raw.name.trim(),
      items: [...raw.items],
      createdAt: raw.createdAt,
      updatedAt: raw.updatedAt
    };
    const prior = byId.get(incoming.id);
    if (!prior) {
      byId.set(incoming.id, incoming);
      imported += 1;
    } else if (incoming.updatedAt > prior.updatedAt) {
      byId.set(incoming.id, incoming);
      updated += 1;
    } else {
      skipped += 1;
    }
  }

  const writeOk = imported + updated === 0
    ? true
    : writeAll(Array.from(byId.values()));

  return { imported, updated, skipped, writeOk };
}
